import React from "react";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Table from "components/common/Table";
import Button from "components/common/Button";
import Pagination from "components/common/Pagination";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";
import BillingTransactionMobileCard from "./BillingTransactionMobileCard";
import BillingFilters from "./BillingFilters";
import {
  STATUS_COLOR,
  getBillingPlanType,
  getBillingProductName,
  getBillingStatusLabel,
} from "./billingHelpers";

const CELL_SX = {
  fontSize: { xs: 13, md: 15 },
  fontWeight: 500,
  color: "#111827",
  lineHeight: 1.4,
};

const IconSquareButton = ({ onClick, ariaLabel, src, rotate = 0 }) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      width: 40,
      height: 40,
      borderRadius: "10px",
      border: "1px solid #E5E7EB",
      bgcolor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      p: 0,
      flexShrink: 0,
      "&:hover": { bgcolor: "#F9FAFB", borderColor: "#CBD5E1" },
    }}
  >
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: 20,
        height: 20,
        transform: rotate ? `rotate(${rotate}deg)` : "none",
      }}
    />
  </Box>
);

const columns = [
  {
    field: "createdAt",
    headerText: "Transaction Date",
    bodyCellComponent: (v) => (
      <Text sx={CELL_SX}>
        {v ? dateTimeHelper.format(v, "$DD/$MM/$YYYY") : "NA"}
      </Text>
    ),
  },
  {
    field: "productName",
    headerText: "Product Name",
    bodyCellComponent: (_v, row) => (
      <Text sx={CELL_SX}>{getBillingProductName(row)}</Text>
    ),
  },
  {
    field: "planType",
    headerText: "Plan Type",
    bodyCellComponent: (_v, row) => (
      <Text sx={CELL_SX}>{getBillingPlanType(row)}</Text>
    ),
  },
  {
    field: "paymentStatus",
    headerText: "Payment Status",
    bodyCellComponent: (v) => (
      <Text
        sx={{
          ...CELL_SX,
          color: STATUS_COLOR[v] || "#111827",
        }}
      >
        {getBillingStatusLabel(v)}
      </Text>
    ),
  },
  {
    field: "paymentAmount",
    headerText: "Subscription Fee",
    bodyCellComponent: (v) => (
      <Text sx={CELL_SX}>
        {v ? formatHelper.currency(v) : formatHelper.currency(0)}
      </Text>
    ),
  },
];

/**
 * Figma 7949:97672 — Billing Details / Transaction History.
 */
const BillingHistoryPage = ({
  onBack,
  filterData,
  transactionStatusOptions = [],
  onFilterChange,
  onReplaceFilters,
  onClearFilters,
  isFilterApplied,
  transactionHistory = [],
  transactionHistoryCount = 0,
  transactionHistoryLoading = false,
  page = 1,
  limit = 25,
  onPageChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: 2, sm: 3, md: 4, lg: 8 }, py: { xs: 2.5, md: 4 } }}
    >
      <Stack spacing={{ xs: 2, md: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <IconSquareButton
            ariaLabel="Back to profile"
            onClick={onBack}
            src="/images/profile-v2/billing/arrow-right.svg"
            rotate={180}
          />
          <Text
            sx={{
              fontWeight: 700,
              fontSize: { xs: 20, sm: 22, md: 24 },
              color: "#111827",
              lineHeight: 1.25,
            }}
          >
            Billing Details
          </Text>
        </Stack>

        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: { xs: "14px", md: "16px" },
            boxShadow: "0px 4px 10px rgba(0,0,0,0.02)",
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.75}
            sx={{ mb: { xs: 2, md: 2.5 } }}
          >
            <Text
              sx={{
                fontWeight: 600,
                fontSize: { xs: 16, md: 18 },
                color: "#374151",
                lineHeight: 1.3,
                flexShrink: 0,
              }}
            >
              Transaction History
            </Text>

            <BillingFilters
              isMobile={isMobile}
              filterData={filterData}
              transactionStatusOptions={transactionStatusOptions}
              onFilterChange={onFilterChange}
              onReplaceFilters={onReplaceFilters}
              onClearFilters={onClearFilters}
              isFilterApplied={isFilterApplied}
            />
          </Stack>

          {!transactionHistory.length ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
              {isFilterApplied ? (
                <>
                  <Text sx={{ mb: 1.5, color: "#6B7280", fontSize: 14 }}>
                    No transaction record found for applied filter
                  </Text>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onClearFilters}
                  >
                    Clear Filter
                  </Button>
                </>
              ) : (
                <Text sx={{ fontWeight: 500, color: "#6B7280", fontSize: 14 }}>
                  {transactionHistoryLoading
                    ? "Loading transactions…"
                    : "No transaction record found"}
                </Text>
              )}
            </Stack>
          ) : !isMobile ? (
            <Box
              sx={{
                overflowX: "auto",
                "& thead th": {
                  backgroundColor: "#fff !important",
                },
                "& thead th .MuiSvgIcon-root": {
                  fontSize: 18,
                  color: "#9CA3AF",
                  opacity: 0.85,
                },
              }}
            >
              <Table
                columns={columns}
                count={transactionHistoryCount}
                data={transactionHistory}
                loading={transactionHistoryLoading}
                paginationBottom
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={onPageChange}
                th_sx={{
                  p: "10px 12px 12px 0",
                  backgroundColor: "#fff",
                  color: "#9CA3AF",
                  borderBottom: "1px solid #E5E7EB",
                  borderRight: "none",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
                td_sx={{
                  p: "14px 12px 14px 0",
                  borderRight: "none",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "15px",
                  fontWeight: 500,
                  verticalAlign: "middle",
                }}
              />
            </Box>
          ) : (
            <Box>
              <Pagination
                count={transactionHistoryCount}
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={onPageChange}
              />
              {transactionHistory.map((data, index) => (
                <Box
                  key={
                    data?.customerPaymentId ||
                    data?.createdAt ||
                    `${data?.productCode}-${index}`
                  }
                  sx={{
                    py: 1.75,
                    borderBottom:
                      index < transactionHistory.length - 1
                        ? "1px solid #E5E7EB"
                        : "none",
                  }}
                >
                  <BillingTransactionMobileCard data={data} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default BillingHistoryPage;
